import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

export const noteKeys = {
  forSubject: (subjectType: string, subjectId: string) =>
    ["notes", subjectType, subjectId] as const,
};

export function useNotes(subjectType: string, subjectId: string | undefined) {
  return useQuery({
    queryKey: noteKeys.forSubject(subjectType, subjectId ?? ""),
    enabled: Boolean(subjectId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/notes", {
        params: { query: { subject_type: subjectType, subject_id: subjectId! } },
      });
      if (error || !data) throw new Error("Failed to load notes");
      return data;
    },
  });
}

export function useAddNote(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; subject?: string }) => {
      const { data, error } = await apiClient.POST("/api/v1/notes", {
        body: {
          subject_type: subjectType,
          subject_id: subjectId,
          subject: input.subject || null,
          body: input.body,
        },
      });
      if (error || !data) throw new Error("Failed to add note");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: noteKeys.forSubject(subjectType, subjectId) }),
  });
}

export function useDeleteNote(subjectType: string, subjectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await apiClient.DELETE("/api/v1/notes/{note_id}", {
        params: { path: { note_id: noteId } },
      });
      if (error) throw new Error("Failed to delete note");
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: noteKeys.forSubject(subjectType, subjectId) }),
  });
}
